package com.matchpredictor.web

import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLElement

enum class EdgeFadeAxis { HORIZONTAL, VERTICAL }

private const val VIEWPORT_FADE_MAX_PX = 88.0
private var viewportFadeEnabled = false
private var viewportFadeBound = false

/**
 * Полноэкранный вертикальный fade для линии/ачивок (скролл документа).
 * Без отдельного scroll-box — не даёт пятна на градиенте body.
 */
internal fun setViewportEdgeFadeEnabled(enabled: Boolean) {
    viewportFadeEnabled = enabled
    val top = ensureViewportFadeEl("viewport-edge-fade-top", "viewport-edge-fade top")
    val bottom = ensureViewportFadeEl("viewport-edge-fade-bottom", "viewport-edge-fade bottom")
    if (!enabled) {
        top.hidden = true
        bottom.hidden = true
        top.style.height = "0px"
        bottom.style.height = "0px"
        return
    }
    top.hidden = false
    bottom.hidden = false
    if (!viewportFadeBound) {
        viewportFadeBound = true
        window.addEventListener("scroll", { syncViewportEdgeFade() })
        window.addEventListener("resize", { syncViewportEdgeFade() })
    }
    window.requestAnimationFrame {
        syncViewportEdgeFade()
        window.requestAnimationFrame { syncViewportEdgeFade() }
    }
}

private fun ensureViewportFadeEl(id: String, className: String): HTMLElement {
    val existing = document.getElementById(id) as? HTMLElement
    if (existing != null) return existing
    val el = document.createElement("div") as HTMLElement
    el.id = id
    el.className = className
    el.setAttribute("aria-hidden", "true")
    document.body?.appendChild(el)
    return el
}

private fun syncViewportEdgeFade() {
    if (!viewportFadeEnabled) return
    val root = document.documentElement ?: return
    val maxScroll = (root.scrollHeight - root.clientHeight).toDouble().coerceAtLeast(0.0)
    val rawY = (window.asDynamic().pageYOffset as? Number)?.toDouble()
        ?: (window.asDynamic().scrollY as? Number)?.toDouble()
        ?: 0.0
    val pos = rawY.coerceIn(0.0, maxScroll)
    val fadeT = pos.coerceAtMost(VIEWPORT_FADE_MAX_PX)
    val fadeB = (maxScroll - pos).coerceAtMost(VIEWPORT_FADE_MAX_PX)
    val top = document.getElementById("viewport-edge-fade-top") as? HTMLElement
    val bottom = document.getElementById("viewport-edge-fade-bottom") as? HTMLElement
    top?.style?.height = "${fadeT}px"
    bottom?.style?.height = "${fadeB}px"
}

/**
 * Плавное затухание у краёв скролл-элемента.
 * Горизонталь: `--fade-l/r` (чипы лиг, mask).
 * Вертикаль: `--fade-t/b` (чат, mask на однородном фоне).
 */
internal fun bindScrollEdgeFade(
    el: HTMLElement,
    axis: EdgeFadeAxis,
    fadeMaxPx: Double = 96.0,
    varsTarget: HTMLElement = el
) {
    val attr = when (axis) {
        EdgeFadeAxis.HORIZONTAL -> "data-edge-fade-x"
        EdgeFadeAxis.VERTICAL -> "data-edge-fade-y"
    }

    fun sync() {
        when (axis) {
            EdgeFadeAxis.HORIZONTAL -> {
                val maxScroll = (el.scrollWidth - el.clientWidth).toDouble().coerceAtLeast(0.0)
                val pos = el.scrollLeft.toDouble().coerceIn(0.0, maxScroll)
                varsTarget.style.setProperty("--fade-l", "${pos.coerceAtMost(fadeMaxPx)}px")
                varsTarget.style.setProperty("--fade-r", "${(maxScroll - pos).coerceAtMost(fadeMaxPx)}px")
            }
            EdgeFadeAxis.VERTICAL -> {
                val maxScroll = (el.scrollHeight - el.clientHeight).toDouble().coerceAtLeast(0.0)
                val pos = el.scrollTop.toDouble().coerceIn(0.0, maxScroll)
                varsTarget.style.setProperty("--fade-t", "${pos.coerceAtMost(fadeMaxPx)}px")
                varsTarget.style.setProperty("--fade-b", "${(maxScroll - pos).coerceAtMost(fadeMaxPx)}px")
            }
        }
    }

    if (el.getAttribute(attr) != "1") {
        el.setAttribute(attr, "1")
        el.addEventListener("scroll", { sync() })
        window.addEventListener("resize", { sync() })
    }
    window.requestAnimationFrame {
        sync()
        window.requestAnimationFrame { sync() }
    }
}
